import { ELEMENT_TYPE } from '@/constants';
import { useFontStore, useProjectStore } from '@/store';
import type { BookPageData, FontInfo, FontLange, FontResourceItem, TextContent } from '@/types';
import { extraFileName, hideLoading, runThreadPool, showLoading } from '@/utils';
import { miniFont } from './FontMiniManager';
import { uploadBookFont } from '@/api/book';
import axios from 'axios';
import { storeToRefs } from 'pinia';
import { toRaw } from 'vue';
import { FONT_DOWNLOAD_BASE_URL, FONT_THUMB_BASE_URL } from '@/constants/apiConstants';
import { resolveAppStaticPath, resolveStaticBase } from '@/utils/staticAsset';

export class FontManager {
  private static _instance: FontManager;

  private _showRegisterLoading = true;

  private fontConfigs = {
    cn: { url: '/font/cn.json', key: 'cn', lang: 'zh' as const },
    zhcn: { url: '/font/zhcn.json', key: 'zhcn', lang: 'zh' as const },
    en: { url: '/font/en.json', key: 'en', lang: 'en' as const }
  };

  private constructor() {}

  public static getInstance(): FontManager {
    if (!FontManager._instance) {
      FontManager._instance = new FontManager();
    }
    return FontManager._instance;
  }

  public setShowLoading(show: boolean): void {
    this._showRegisterLoading = show;
  }

  public async loadLocalFonts() {}

  /**
   * 加载并注册默认字体（读取 font/default/fonts.json）
   */
  public async loadDefaultFonts() {
    const fontStore = useFontStore();
    const { appFonts } = storeToRefs(fontStore);

    try {
      // 步骤1：相对 reader 包根（或工作台 BASE_URL）拉取默认字体清单
      const staticBase = resolveStaticBase();
      const url = `${staticBase}font/default/fonts.json`;
      const response = await axios.get(url);
      const data = response.data as Array<{
        label: string;
        href: string;
        postfix: string;
        value: string;
        lang: string;
        thumb: string;
      }>;

      if (!Array.isArray(data)) return;

      const fonts: FontInfo[] = data.map((item) => ({
        fontFamily: item.value,
        label: item.label,
        lang: item.lang as FontLange,
        path: `${staticBase}${item.href}`,
        apply: 'editor',
        type: 'app' as const,
        thumb: item.thumb || undefined
      }));

      // 记录到 appFonts
      appFonts.value = new Map(fonts.map((font) => [font.fontFamily, font]));
    } catch (error) {
      console.error('加载默认字体列表失败', error);
    }
  }

  public async loadAllOnlineFonts() {
    const tabs = Object.keys(this.fontConfigs) as (keyof typeof this.fontConfigs)[];
    await Promise.all(tabs.map((tab) => this.loadOnlineFonts(tab)));
  }

  public async loadOnlineFonts(tab: keyof typeof this.fontConfigs) {
    const fontStore = useFontStore();
    const { cnOnlineFonts, zhcnOnlineFonts, enOnlineFonts } = storeToRefs(fontStore);

    const storeRefMap = {
      cn: cnOnlineFonts,
      zhcn: zhcnOnlineFonts,
      en: enOnlineFonts
    };

    const storeRef = storeRefMap[tab];
    if (storeRef.value.size > 0) return;

    const config = this.fontConfigs[tab];

    try {
      // 需拼上 resolveStaticBase（dev 为 /，test 为 /dashboard/，阅读端为 reader 包根）。
      const url = `${resolveStaticBase()}${config.url.replace(/^\//, '')}`;
      const response = await axios.get(url);
      const data = response.data as Record<string, FontResourceItem[]>;

      if (data[config.key] && Array.isArray(data[config.key])) {
        const result: FontInfo[] = data[config.key].map((item) => ({
          fontFamily: item.value,
          label: item.label,
          lang: config.lang as FontLange,
          apply: 'editor',
          path: `${FONT_DOWNLOAD_BASE_URL}${item.value}.${item.postfix}`,
          type: 'online' as const,
          thumb: `${FONT_THUMB_BASE_URL}${item.value}.png`
        }));

        storeRef.value = new Map(result.map((font) => [font.fontFamily, font]));
      }
    } catch (error) {
      console.error(`加载字体列表失败: ${config.url}`, error);
    }
  }

  public async registerFont(...fonts: FontInfo[]) {
    if (!fonts || fonts.length === 0) return false;

    const { registerFonts } = storeToRefs(useFontStore());

    if (this._showRegisterLoading) {
      showLoading({ text: '加载字体中...' });
    }

    // 并行加载并注册每条字体，单条失败不影响其余
    const results = await runThreadPool(fonts, async (font) => {
      if (!font || !font.fontFamily || !font.path) {
        return false;
      }

      if (registerFonts.value.has(font.fontFamily)) {
        return true;
      }

      try {
        let path = font.path;

        if (font.type === 'app') {
          // 内置字体相对路径拼上包根；已是绝对地址则不再拼接（避免 loadDefaultFonts 双重前缀）
          path = resolveAppStaticPath(font.path);
        }

        // 阅读端优先用子集化后的 readerPath
        if (font.apply === 'reader' && import.meta.env.APP === 'reader' && font.readerPath) {
          path = resolveAppStaticPath(font.readerPath);
        }

        // console.log('registerFontPath', path);

        const fontUrl = `url('${path}')`;
        const face = new FontFace(font.fontFamily, fontUrl);
        const loadedFace = await face.load();

        document.fonts.add(loadedFace);
        registerFonts.value.set(font.fontFamily, font);

        console.info(`字体:${font.label || font.fontFamily} 注册成功`);
        return true;
      } catch (error) {
        console.error(`字体:${font.label || font.fontFamily} 注册失败`, { error });
        return false;
      }
    });

    if (this._showRegisterLoading) {
      hideLoading();
    }

    return results.some(Boolean);
  }

  /**
   * 确保字体已注册
   * @param fontFamily
   */
  public async ensureFontRegistered(fontFamily: string) {
    if (!fontFamily) return false;

    const { extFonts, registerFonts } = storeToRefs(useFontStore());

    // 再判断是否是已注册的拓展字体
    if (registerFonts.value.has(fontFamily)) return true;

    const font = extFonts.value.get(fontFamily);

    if (!font) return false;

    const flag = await this.registerFont(font);

    return flag;
  }

  public getFontInfo(fontFamily: string) {
    const fontStore = useFontStore();

    return fontStore.getFontInfo(fontFamily);
  }

  /**
   * 从页面数据中提取字体与其对应文本内容的映射
   * @param pages 页面数据数组
   * @returns fontFamily → 去重文本字符 的映射
   */
  public getFontTextMapFromPages(pages: BookPageData[]): Map<string, string> {
    const fontTextMap = new Map<string, string>();

    if (!pages) return fontTextMap;

    const fontCharsMap = new Map<string, Set<string>>();

    for (const page of pages) {
      if (!page.actions) continue;

      for (const action of page.actions) {
        const element = action.element;
        if (!element || element.type !== ELEMENT_TYPE.TEXT) continue;

        const textContent = element.content as TextContent | undefined;
        const fontFamily = textContent?.style?.font?.fontFamily;
        const text = textContent?.text;

        if (!fontFamily || !text) continue;

        // fontFamily 可能是字符串或字符串数组
        const families = Array.isArray(fontFamily) ? fontFamily : [fontFamily];

        for (const family of families) {
          if (!family) continue;

          // 收集每个字体对应的文本字符（去重）
          if (!fontCharsMap.has(family)) {
            fontCharsMap.set(family, new Set<string>());
          }
          for (const char of text) {
            fontCharsMap.get(family)!.add(char);
          }
        }
      }
    }

    // 将去重后的字符集合转为字符串
    for (const [family, chars] of fontCharsMap) {
      fontTextMap.set(family, Array.from(chars).join(''));
    }

    return fontTextMap;
  }

  public getProjectFonts(): Map<string, string> {
    const projectStore = useProjectStore();
    return this.getFontTextMapFromPages(projectStore.pages);
  }

  /**
   * 对项目中文字体进行 fontmini 子集化并上传，返回最终的字体信息列表。
   * 会与上次快照逐字体对比，文本未变的字体直接复用缓存，只对变化的字体重新子集化。
   *
   * - 传入 projectId 时：中文字体走 fontmini 缩减 → 上传，英文字体直接返回原始 FontInfo
   * - 不传 projectId 时：所有字体直接返回原始 FontInfo（跳过子集化）
   *
   * @param fontTextMap - 字体 family → 文本内容 的映射（来自 getFontTextMapFromPages()）
   * @param projectId - 项目 ID，不传则跳过 fontmini
   * @returns 最终的字体信息列表
   */
  public async processFontSubset(
    fontTextMap: Map<string, string>,
    projectId?: string | number,
    useAppFonts = false
  ): Promise<FontInfo[]> {
    const fontStore = useFontStore();
    const projectStore = useProjectStore();

    const storedMap = projectStore.fontTextMap;
    const cachedFonts = projectStore.getLastFonts();
    const cachedByFamily = new Map(cachedFonts.map((f) => [f.fontFamily, f]));

    // 逐字体与快照对比：未变化的复用缓存，有变化的收集到 changedMap
    const changedMap = new Map<string, string>();
    const reusedFonts: FontInfo[] = [];

    for (const [family, text] of fontTextMap) {
      const cached = cachedByFamily.get(family);
      if (cached && storedMap.get(family) === text) {
        reusedFonts.push(cached);
      } else {
        changedMap.set(family, text);
      }
    }

    // 只对有变化的字体走子集化流程
    let processedFonts: FontInfo[] = [];
    if (changedMap.size > 0) {
      const entries = Array.from(changedMap.entries());

      const results = await runThreadPool(entries, async ([fontFamily, text]) => {
        const fontInfo = useAppFonts
          ? fontStore.appFonts.get(fontFamily)
          : fontStore.registerFonts.get(fontFamily);
        if (!fontInfo) {
          console.warn(`字体 ${fontFamily} 未注册，跳过`);
          return null;
        }

        // 未传 projectId 时跳过子集化，直接返回原始 FontInfo
        if (!projectId) {
          return toRaw(fontInfo);
        }

        // 英文字体不做缩减，直接返回原始 FontInfo
        if (fontInfo.lang === 'en') {
          return toRaw(fontInfo);
        }

        // 中文字体走 fontmini 子集化
        const rawFileName = extraFileName(fontInfo.path);
        if (!rawFileName) {
          console.warn(`无法获取字体 ${fontFamily} 的文件名，使用原始字体`);
          return toRaw(fontInfo);
        }

        // app 字体统一使用 ttf 后缀
        const fontFileName =
          fontInfo.type === 'app' ? extraFileName(rawFileName, false) + '.ttf' : rawFileName;

        try {
          const result = await miniFont.getMiniFont(fontFileName, text);
          if (!result) {
            // getMiniFont 返回空（正在请求中或文本为空），使用原始字体
            return toRaw(fontInfo);
          }

          const fontResult = await uploadBookFont(projectId, fontFileName, result);
          // console.log(`字体 ${fontFamily} 子集化上传成功`, fontResult);

          // 上传成功，apply 改为 reader，记录 relativePath
          return {
            ...toRaw(fontInfo),
            apply: 'reader' as const,
            readerPath: fontResult.url
          };
        } catch (error) {
          console.error(`字体 ${fontFamily} 子集化失败，使用原始字体`, error);
          return toRaw(fontInfo);
        }
      });

      processedFonts = results.filter((item): item is FontInfo => item !== null);
    }

    const allFonts = [...reusedFonts, ...processedFonts];

    // 更新快照，下次保存以此为基线
    projectStore.setFontSnapshot(fontTextMap, allFonts);

    return allFonts;
  }
}

export const fontManager = FontManager.getInstance();
